import { IconZoomScan } from "@tabler/icons-react";

import type { ICellRendererParams } from "ag-grid-community";
import { useAuthorization } from "~/contexts/authorizationProvider";
import { entityDrillViewInScopeForRow, PER_RECORD_SCOPE_KINDS } from "~/shared/ai/entity-link";
import { ActionIcon, Tooltip } from "~/ui";
import { useOrgRouter } from "~/utils/paths";
import type { ReportFieldDefinition, ReportRow } from "~/utils/types/assistant-report-artifact";

import { entityDrillTarget, entityLinkAllowed } from "./entity-drill";

type EntityDrillParams = ICellRendererParams<ReportRow, unknown> & {
  entityRef: NonNullable<ReportFieldDefinition["entityRef"]>;
};

// Hover drill-down for report cells that label a linkable record, cloned from the Income
// Statement account drill: label + revealed-on-row-hover zoom icon that opens the record
// in a new tab so the analysis stays put.
export const EntityDrillCellRenderer = (params: EntityDrillParams) => {
  const router = useOrgRouter();
  const { hasPermission } = useAuthorization();
  const row: ReportRow = params.data ?? {};
  const kind = params.entityRef.entity ?? (params.entityRef.typeKey ? row[params.entityRef.typeKey] : null);
  const idKey = params.entityRef.idKey;
  // Customers, sales orders and purchase orders depend on the record, so the server's per-row flag decides.
  const perRecordScope = PER_RECORD_SCOPE_KINDS.some(scopedKind => scopedKind === kind);
  const recordInScope = !perRecordScope || entityDrillViewInScopeForRow(row, idKey);
  // A purchase order linked to a sales order the actor can open is readable without the module permission.
  const permissionOk = entityLinkAllowed(kind, hasPermission) || (kind === "purchase_order" && recordInScope);
  const target =
    recordInScope && permissionOk
      ? entityDrillTarget(kind, row[idKey], params.value, params.entityRef.routeIdKey ? row[params.entityRef.routeIdKey] : undefined)
      : null;
  const label = params.value === null || params.value === undefined ? "" : String(params.value);

  if (!target) {
    return <span>{label}</span>;
  }

  const handleDrill = (event: React.MouseEvent) => {
    event.stopPropagation();
    const path = router.makeOrgPath(target.href);
    window.open(typeof path === "string" ? path : target.href, "_blank", "noopener");
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        width: "100%",
        minWidth: 0,
      }}
    >
      <span style={{ flex: 1, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{label}</span>
      <Tooltip label={target.label} withinPortal openDelay={200}>
        <ActionIcon
          size="sm"
          variant="subtle"
          color="blue"
          aria-label={target.label}
          onClick={handleDrill}
          className="entity-drill-action"
          style={{
            flexShrink: 0,
            opacity: 0,
            pointerEvents: "none",
            transition: "opacity 0.15s ease",
          }}
        >
          <IconZoomScan size={16} />
        </ActionIcon>
      </Tooltip>
    </div>
  );
};
